import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, Download, Expand, FileText, Heart, Minus, Plus, ShieldCheck, ShoppingCart, Sparkles } from "lucide-react";
import { api, czk, fmtSize, media, priceTxt } from "../lib/api";
import { useCart, useSite } from "../lib/store";
import { Daisy } from "../components/Daisy";
import { ProductCard } from "../components/ProductCard";
import { useSeo } from "../components/Seo";
import { Lightbox } from "../components/Lightbox";

const Gallery = ({ images, name }) => {
  const [i, setI] = useState(0);
  const [open, setOpen] = useState(false);
  if (!images?.length) return <div className="aspect-square rounded-[2rem] bg-[#E9EFE0]" />;
  return (
    <div>
      <button type="button" onClick={() => setOpen(true)} aria-label="Zobrazit fotku přes celou obrazovku" data-testid="product-image-open"
        className="group relative block aspect-square w-full cursor-zoom-in overflow-hidden rounded-[2rem] border-[6px] border-white bg-[#EFEBE1] shadow-lg">
        <AnimatePresence mode="wait">
          <motion.img key={i} src={media(images[i])} alt={name} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}
            className="h-full w-full object-contain" data-testid="product-main-image" />
        </AnimatePresence>
        <span className="absolute bottom-3 right-3 flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1.5 text-xs font-semibold text-[#3E4436] shadow transition-transform group-hover:scale-105"><Expand className="h-3.5 w-3.5" /> Zvětšit</span>
      </button>
      <AnimatePresence>{open && <Lightbox images={images} index={i} setIndex={setI} onClose={() => setOpen(false)} name={name} />}</AnimatePresence>
      {images.length > 1 && (
        <div className="mt-4 flex gap-3 overflow-x-auto no-scrollbar">
          {images.map((im, k) => (
            <button key={k} onClick={() => setI(k)} data-testid={`product-thumb-${k}`} className={`h-20 w-20 shrink-0 overflow-hidden rounded-2xl border-2 transition-all ${k === i ? "border-[#7A8460] scale-105" : "border-transparent opacity-70"}`}>
              <img src={media(im)} alt="" className="h-full w-full bg-[#EFEBE1] object-contain" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const MobileBuyBar = ({ target, p, price, sold, onAdd }) => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const f = () => setShow(!!target.current && target.current.getBoundingClientRect().bottom < 70);
    f();
    window.addEventListener("scroll", f, { passive: true });
    return () => window.removeEventListener("scroll", f);
  }, [target, p.id]);
  return (
    <AnimatePresence>
      {show && (
        <motion.div initial={{ y: 100 }} animate={{ y: 0 }} exit={{ y: 100 }} transition={{ type: "spring", damping: 24 }}
          className="fixed inset-x-0 bottom-0 z-[45] flex items-center gap-3 border-t bg-[#FFFDF8]/95 px-4 pt-3 shadow-[0_-10px_30px_-15px_rgba(45,52,38,.4)] backdrop-blur-xl md:hidden"
          style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }} data-testid="mobile-buy-bar">
          <div className="min-w-0 flex-1"><p className="truncate text-xs text-[#6E7565]">{p.name}</p><p className="font-serif text-xl font-black">{priceTxt(price)}</p></div>
          <button className="btn-sage !py-3.5" disabled={sold} onClick={onAdd} data-testid="mobile-buy-bar-add"><ShoppingCart className="h-4 w-4" /> Do košíku</button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default function Product() {
  const { slug } = useParams();
  const { site } = useSite();
  const { add, favs, toggleFav } = useCart();
  const [data, setData] = useState(null);
  const [qty, setQty] = useState(1);
  const [err, setErr] = useState(false);
  const buyRef = useRef(null);
  useEffect(() => {
    setData(null);
    setQty(1);
    api.get(`/products/${slug}`).then((r) => setData(r.data)).catch(() => setErr(true));
  }, [slug]);
  const p = data?.product;
  useSeo({
    title: p?.name, description: p?.short_desc, image: p?.images?.[0] ? media(p.images[0]) : undefined,
    jsonLd: p ? { "@context": "https://schema.org", "@graph": [
      { "@type": "Product", name: p.name, description: p.short_desc || p.description, image: (p.images || []).map(media), sku: p.id, brand: { "@type": "Brand", name: "Hravá mateřinka" },
        offers: { "@type": "Offer", url: window.location.href, priceCurrency: "CZK", price: p.sale_price || p.price, availability: p.stock !== null && p.stock !== undefined && p.stock <= 0 ? "https://schema.org/OutOfStock" : "https://schema.org/InStock" } },
      { "@type": "BreadcrumbList", itemListElement: [["Domů", "/"], ["E-shop", "/eshop"], [p.name, `/produkt/${p.slug}`]].map(([n, u], i) => ({ "@type": "ListItem", position: i + 1, name: n, item: window.location.origin + u })) },
    ] } : undefined,
  });
  if (err) return <div className="py-32 text-center"><p className="h-section text-3xl">Produkt nenalezen</p><Link to="/eshop" className="btn-sage mt-6">Zpět do e-shopu</Link></div>;
  if (!p) return <div className="grid min-h-[60vh] place-items-center"><Daisy size={70} className="animate-spin" /></div>;
  const sale = p.sale_price && p.sale_price < p.price;
  const sold = p.stock !== null && p.stock !== undefined && p.stock <= 0;
  const fav = favs.some((f) => f.id === p.id);
  const cats = (site?.categories || []).filter((c) => p.category_ids?.includes(c.id));
  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-8" data-testid="product-page">
      <MobileBuyBar target={buyRef} p={p} price={sale ? p.sale_price : p.price} sold={sold} onAdd={() => add(p, qty)} />
      <Link to="/eshop" className="mb-6 inline-flex items-center gap-2 text-sm text-[#6E7565] hover:text-[#7A8460]" data-testid="product-back-link"><ArrowLeft className="h-4 w-4" /> Zpět do e-shopu</Link>
      <div className="grid gap-10 md:grid-cols-2">
        <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }}><Gallery images={p.images} name={p.name} /></motion.div>
        <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }}>
          <div className="flex flex-wrap gap-2">
            {cats.map((c) => <Link key={c.id} to={`/kategorie/${c.slug}`} className="rounded-full bg-[#E9EFE0] px-3 py-1 text-xs font-semibold text-[#7A8460]">{c.name}</Link>)}
            {p.is_new && <span className="rounded-full bg-[#A3AC86] px-3 py-1 text-xs font-semibold text-white">Novinka</span>}
          </div>
          <h1 className="h-display mt-4 text-4xl leading-tight sm:text-5xl" data-testid="product-title">{p.name}</h1>
          {p.short_desc && <p className="mt-4 text-lg text-[#2D3426]/80">{p.short_desc}</p>}
          <div className="mt-6 flex items-end gap-3">
            <span className="font-serif text-4xl font-black text-[#2D3426]" data-testid="product-detail-price">{priceTxt(sale ? p.sale_price : p.price)}</span>
            {sale && <span className="mb-1 text-lg text-[#6E7565] line-through">{czk(p.price)}</span>}
            {sale && <span className="mb-1 rounded-full bg-[#D96B43] px-3 py-1 text-xs font-bold text-white">Sleva {Math.round(100 - (p.sale_price / p.price) * 100)} %</span>}
          </div>
          <div className="mt-6 grid grid-cols-3 gap-3 text-center text-xs">
            {[["Formát", p.format || "PDF"], ["Věk", p.age || "3–7 let"], ["Stran", p.pages || "–"]].map(([k, v]) => (
              <div key={k} className="rounded-2xl bg-white p-3"><p className="text-[#6E7565]">{k}</p><p className="mt-1 font-semibold text-[#2D3426]">{v}</p></div>
            ))}
          </div>
          {p.stock !== null && p.stock !== undefined && <p className="mt-4 text-sm text-[#7A8460]" data-testid="product-stock">{sold ? "Momentálně vyprodáno" : `Dostupné licence: ${p.stock} ks`}</p>}
          <div ref={buyRef} className="mt-6 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 rounded-full border bg-white p-1.5">
              <button className="grid h-9 w-9 place-items-center rounded-full bg-[#F7F3EA]" onClick={() => setQty(Math.max(1, qty - 1))} data-testid="product-qty-dec"><Minus className="h-4 w-4" /></button>
              <span className="w-6 text-center font-semibold" data-testid="product-qty">{qty}</span>
              <button className="grid h-9 w-9 place-items-center rounded-full bg-[#F7F3EA]" onClick={() => setQty(Math.min(99, qty + 1))} data-testid="product-qty-inc"><Plus className="h-4 w-4" /></button>
            </div>
            <button className="btn-sage flex-1 !py-4" disabled={sold} onClick={() => add(p, qty)} data-testid="add-to-cart-button"><ShoppingCart className="h-4 w-4" /> Přidat do košíku</button>
            <button onClick={() => toggleFav(p)} className="grid h-12 w-12 place-items-center rounded-full border bg-white transition-transform hover:scale-110" data-testid="product-fav-btn"><Heart className={`h-5 w-5 ${fav ? "fill-[#D96B43] text-[#D96B43]" : ""}`} /></button>
          </div>
          <div className="mt-6 space-y-2 rounded-3xl bg-[#E9EFE0] p-5 text-sm">
            <p className="flex items-center gap-2"><Download className="h-4 w-4 text-[#7A8460]" /> Ke stažení v sekci Můj účet ihned po zaplacení</p>
            <p className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#7A8460]" /> Bezpečná platba kartou nebo QR kódem</p>
            <p className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#7A8460]" /> Bez dopravy – digitální produkt</p>
          </div>
          {p.files?.length > 0 && (
            <div className="mt-4">
              <p className="text-sm font-semibold">Obsah balíčku</p>
              <ul className="mt-2 space-y-1 text-sm text-[#6E7565]">{p.files.map((f) => <li key={f.id} className="flex items-center gap-2"><FileText className="h-4 w-4" /> {f.name} <span className="text-xs">{fmtSize(f.size)}</span></li>)}</ul>
            </div>
          )}
        </motion.div>
      </div>
      {p.description && (
        <section className="card-soft mt-12 p-6 sm:p-10">
          <h2 className="h-section text-2xl">Popis produktu</h2>
          <div className="mt-4 whitespace-pre-line leading-relaxed text-[#2D3426]/90" data-testid="product-description">{p.description}</div>
        </section>
      )}
      <section className="mt-8 rounded-3xl border border-[#E4D9C3] bg-[#FBF7EE] p-6 sm:p-8" data-testid="product-license-banner">
        <div className="flex items-start gap-3">
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-[#B98B4E]" />
          <div className="space-y-3 text-sm leading-relaxed text-[#4A4335]">
            <h3 className="font-serif text-lg font-bold text-[#2D3426]">Licenční podmínky a omezení použití</h3>
            <p>Zakoupený digitální produkt slouží výhradně k osobním účelům kupujícího.</p>
            <div>
              <p className="font-semibold">Je zakázáno:</p>
              <ul className="mt-1 list-disc space-y-1 pl-5">
                <li>produkt distribuovat, sdílet nebo zveřejňovat třetím osobám,</li>
                <li>produkt dále prodávat nebo poskytovat za úplatu či zdarma,</li>
                <li>produkt využívat pro marketingové a komerční účely.</li>
              </ul>
            </div>
            <p>Materiály jsou určeny především fyzickým osobám.</p>
            <p><span className="font-semibold">Využití ve vzdělávání:</span> Chcete-li produkt použít pro vzdělávací účely (výuka, kurzy, workshopy, školy apod.), je nutné předem kontaktovat provozovatele webu a získat jeho písemný souhlas. Provozovatel si vyhrazuje právo pro tento účel upravit cenu i podmínky užití produktu.</p>
            <p>Porušení těchto podmínek může vést k odepření dalšího využívání produktu a k právním krokům ze strany provozovatele.</p>
          </div>
        </div>
      </section>
      {data.related?.length > 0 && (
        <section className="mt-14">
          <h2 className="h-section mb-6 text-3xl">Mohlo by se vám líbit</h2>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">{data.related.map((r, k) => <ProductCard key={r.id} p={r} i={k} />)}</div>
        </section>
      )}
    </div>
  );
}
